import React, { useState, useEffect, useRef } from "react";
import "./CAS2.css";

interface CAS2Props {
  isUnlocked?: boolean;
  onCBS2Primary: () => void;
  onCBS2Secondary?: () => void;
  onChatbotTrigger?: (visible: boolean) => void;
}

const CAS2: React.FC<CAS2Props> = ({
  isUnlocked = true,
  onCBS2Primary: _onCBS2Primary,
  onCBS2Secondary: _onCBS2Secondary,
  onChatbotTrigger,
}) => {
  const [biologyRevealed, setBiologyRevealed] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(
    new Set()
  );
  const [animatedStats, setAnimatedStats] = useState<Set<number>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);
  const cbs2Ref = useRef<HTMLElement>(null);

  // Trigger animations when CAS2 unlocks — scoped to this container
  useEffect(() => {
    if (!isUnlocked) return;

    setVisibleSections(
      new Set(["hook", "stats", "biology", "gap", "power20", "bridge", "cbs2"])
    );

    setTimeout(() => {
      if (!containerRef.current) return;
      const statElements =
        containerRef.current.querySelectorAll(".stat-number");
      statElements.forEach((el, index) => {
        const target = el.getAttribute("data-target");
        if (target && el instanceof HTMLElement) {
          setTimeout(() => {
            animateNumber(el, target, index);
          }, index * 200);
        }
      });
    }, 300);
  }, [isUnlocked]);

  // Chatbot trigger — fires to App.tsx when user reaches bottom section
  useEffect(() => {
    if (!onChatbotTrigger) return;

    const caLane = document.querySelector(".ca-lane");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          onChatbotTrigger(entry.isIntersecting);
        });
      },
      { threshold: 0.3, root: caLane }
    );

    if (cbs2Ref.current) {
      observer.observe(cbs2Ref.current);
    }

    return () => observer.disconnect();
  }, [onChatbotTrigger]);

  const animateNumber = (
    element: HTMLElement,
    targetText: string,
    index: number
  ) => {
    if (animatedStats.has(index)) return;

    const hasPlus = targetText.includes("+");
    const hasPercent = targetText.includes("%");
    const targetNumber = parseFloat(targetText.replace(/[^0-9.]/g, ""));

    if (isNaN(targetNumber)) return;

    setAnimatedStats((prev) => new Set(prev).add(index));

    const duration = 2000;
    const steps = 60;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      const displayValue = targetNumber * eased;

      let displayText = Math.round(displayValue).toString();
      if (hasPlus) displayText += "+";
      if (hasPercent) displayText += "%";

      element.textContent = displayText;

      if (step >= steps) {
        clearInterval(timer);
        element.textContent = targetText;
      }
    }, duration / steps);
  };

  const handleRevealBiology = () => {
    setBiologyRevealed(!biologyRevealed);
  };

  if (!isUnlocked) {
    return (
      <div className="cas2-locked">
        <div className="lock-overlay">
          <div className="lock-content">
            <div className="lock-icon">🔒</div>
            <p>Complete the previous section to continue</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cas2-container" ref={containerRef}>
      {/* Ambient Glow */}
      <div className="ambient-glow">
        <div className="glow-orb glow-orb-1"></div>
        <div className="glow-orb glow-orb-2"></div>
        <div className="glow-orb glow-orb-3"></div>
      </div>

      {/* Opening Hook */}
      <section className="cas2-section hook-section" data-section-id="hook">
        <div className="hook-background"></div>
        <div className="hook-overlay"></div>
        <div
          className={`cas2-content hook ${
            visibleSections.has("hook") ? "visible" : ""
          }`}
        >
          <h1>Believe it or not.</h1>
          <p>The city is designed for everything except rest.</p>
          <div className="hook-heartbeat">
            <canvas
              className="heartbeat-canvas"
              ref={(canvas) => {
                if (!canvas || (canvas as any).__setup) return;
                (canvas as any).__setup = true;

                const ctx = canvas.getContext("2d");
                if (!ctx) return;

                const dpr = window.devicePixelRatio || 1;
                const width = canvas.offsetWidth;
                const height = canvas.offsetHeight;
                canvas.width = width * dpr;
                canvas.height = height * dpr;
                ctx.scale(dpr, dpr);

                const midY = height / 2;
                const totalDuration = 8000;
                let startTime: number | null = null;
                let started = false;

                function drawFrame(now: number) {
                  if (!startTime) startTime = now;
                  const elapsed = now - startTime;
                  const progress = Math.min(elapsed / totalDuration, 1);
                  const drawX = progress * width;

                  ctx!.clearRect(0, 0, width, height);

                  const glowSize = 4 + progress * 25;
                  const glowAlpha = 0.3 + progress * 0.6;

                  ctx!.shadowColor = `rgba(232, 84, 79, ${glowAlpha})`;
                  ctx!.shadowBlur = glowSize;
                  ctx!.strokeStyle = "#e8544f";
                  ctx!.lineWidth = 1.5 + progress * 2;
                  ctx!.lineCap = "round";
                  ctx!.lineJoin = "round";
                  ctx!.globalAlpha = 0.4 + progress * 0.6;

                  ctx!.beginPath();
                  ctx!.moveTo(0, midY);

                  let x = 0;
                  let beatIndex = 0;

                  while (x < drawX) {
                    const beatProgress = x / width;
                    // Flat sections shrink as stress builds
                    const flatLen = 30 - beatProgress * 16;
                    const flatEnd = Math.min(x + flatLen, drawX);
                    ctx!.lineTo(flatEnd, midY);
                    x = flatEnd;
                    if (x >= drawX) break;

                    // Amplitude grows dramatically
                    const intensity = 0.2 + beatProgress * 0.8;
                    const amp = (15 + beatIndex * 5) * intensity;
                    const spikeW = 35 + beatProgress * 10;

                    const p1x = x + spikeW * 0.12;
                    const p2x = x + spikeW * 0.22;
                    const p3x = x + spikeW * 0.35;
                    const p4x = x + spikeW * 0.5;
                    const p5x = x + spikeW * 0.7;
                    const p6x = x + spikeW;

                    if (p6x > drawX) {
                      ctx!.lineTo(drawX, midY);
                      break;
                    }

                    // P wave
                    ctx!.lineTo(p1x, midY - amp * 0.12);
                    ctx!.lineTo(p2x, midY);
                    // QRS complex — the big spike
                    ctx!.lineTo(p3x, midY + amp * 0.25);
                    ctx!.lineTo(p3x + 3, midY - amp);
                    ctx!.lineTo(p4x, midY + amp * 0.4);
                    // T wave
                    ctx!.lineTo(p5x, midY - amp * 0.15);
                    ctx!.lineTo(p6x, midY);

                    x = p6x;
                    beatIndex++;
                  }

                  ctx!.stroke();

                  // Scan dot
                  if (progress < 1) {
                    ctx!.beginPath();
                    ctx!.arc(drawX, midY, 3 + progress * 4, 0, Math.PI * 2);
                    ctx!.fillStyle = `rgba(232, 84, 79, ${
                      0.5 + progress * 0.5
                    })`;
                    ctx!.shadowBlur = 20 + progress * 25;
                    ctx!.fill();
                  }

                  if (progress < 1) {
                    requestAnimationFrame(drawFrame);
                  }
                }

                // Use IntersectionObserver to start when visible
                const observer = new IntersectionObserver(
                  (entries) => {
                    if (entries[0].isIntersecting && !started) {
                      started = true;
                      requestAnimationFrame(drawFrame);
                      observer.disconnect();
                    }
                  },
                  { threshold: 0.3 }
                );
                observer.observe(canvas);
              }}
            />
          </div>
        </div>
      </section>

      {/* Part 1: The Numbers */}
      <section className="cas2-section" data-section-id="stats">
        <div
          className={`cas2-content ${
            visibleSections.has("stats") ? "visible" : ""
          }`}
        >
          <div className="part-header">Did You Know?</div>
          <div className="stats-grid">
            <div
              className={`stat-item ${
                visibleSections.has("stats") ? "visible" : ""
              }`}
            >
              <div className="stat-number" data-target="47">
                0
              </div>
              <div className="stat-text">
                stress episodes experienced by urban professionals per day
              </div>
            </div>
            <div
              className={`stat-item ${
                visibleSections.has("stats") ? "visible" : ""
              }`}
            >
              <div className="stat-number" data-target="11+">
                0
              </div>
              <div className="stat-text">
                hours your nervous system stays in fight-or-flight daily
              </div>
            </div>
            <div
              className={`stat-item ${
                visibleSections.has("stats") ? "visible" : ""
              }`}
            >
              <div className="stat-number" data-target="73%">
                0
              </div>
              <div className="stat-text">
                report feeling "always on" with no off switch
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Part 2: The Biology */}
      <section className="cas2-section" data-section-id="biology">
        <div
          className={`cas2-content biology-content ${
            visibleSections.has("biology") ? "visible" : ""
          }`}
        >
          <div className="part-header">What This Does To You</div>
          <h2>
            Your sympathetic nervous system becomes the default
            <span className="pulse-indicator"></span>
          </h2>

          <button
            className={`reveal-button ${biologyRevealed ? "revealed" : ""}`}
            onClick={handleRevealBiology}
          >
            <span className="reveal-text">Click to reveal the impact</span>
            <span className="reveal-icon">+</span>
          </button>

          <div
            className={`biology-reveal ${biologyRevealed ? "revealed" : ""}`}
          >
            <ul className="biology-list">
              <li>Cortisol levels 3x normal baseline</li>
              <li>Sleep quality decreases 40%</li>
              <li>Decision fatigue sets in by 2pm</li>
              <li>Your body forgets how to genuinely rest</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Part 3: The Gap */}
      <section className="cas2-section" data-section-id="gap">
        <div
          className={`cas2-content gap-content ${
            visibleSections.has("gap") ? "visible" : ""
          }`}
        >
          <div className="part-header">The Missing Infrastructure</div>
          <h2>Cities have:</h2>
          <div className="infrastructure-list">
            Coffee shops for energy
            <br />
            Gyms for physical release
            <br />
            Bars for social decompression
          </div>

          <div className="missing">
            <div className="missing-header">
              But nowhere designed for genuine nervous system reset
            </div>
            <ul className="missing-list">
              <li className={visibleSections.has("gap") ? "visible" : ""}>
                No third space for your parasympathetic system
              </li>
              <li className={visibleSections.has("gap") ? "visible" : ""}>
                No infrastructure for stillness
              </li>
              <li className={visibleSections.has("gap") ? "visible" : ""}>
                No sanctuary between the chaos
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* The Bridge */}
      <section className="cas2-section bridge" data-section-id="bridge">
        <div
          className={`cas2-content ${
            visibleSections.has("bridge") ? "visible" : ""
          }`}
        >
          <p>This isn't a personal failing.</p>
          <h2>It's an infrastructure gap.</h2>
          <p className="bridge-cta">
            We need to take action into our own hands.
          </p>
        </div>
      </section>

      {/* Part 5: The Power of 20 */}
      <section className="cas2-section power-of-20" data-section-id="power20">
        <div
          className={`cas2-content power20-content ${
            visibleSections.has("power20") ? "visible" : ""
          }`}
        >
          <div className="part-header">The Power of 20</div>

          <div className="power20-hero">
            <div className="power20-number">
              20<span className="power20-unit">min</span>
            </div>
            <div className="power20-subtitle">
              the clinically validated window for measurable stress reduction
            </div>
          </div>

          <div className="power20-bpm">
            <span
              className="bpm-value"
              ref={(el) => {
                if (!el || el.dataset.animated) return;
                el.dataset.animated = "true";
                const start = 98;
                const end = 64;
                const duration = 6000;
                const steps = 60;
                let step = 0;
                const interval = setInterval(() => {
                  step++;
                  const progress = step / steps;
                  const eased = 1 - Math.pow(1 - progress, 3);
                  const current = Math.round(start - (start - end) * eased);
                  el.textContent = String(current);
                  // Pulse speed: 0.61s at 98bpm → 0.94s at 64bpm
                  const speed = 60 / current;
                  el.style.setProperty("--bpm-speed", `${speed.toFixed(2)}s`);
                  if (step >= steps) clearInterval(interval);
                }, duration / steps);
              }}
            >
              98
            </span>
            <span className="bpm-label">BPM</span>
          </div>

          <div className="power20-narrative">
            <p>
              Heart rate climbing. Thoughts racing. The morning has already won.
            </p>
            <p>
              You reach for your phone. Open the app. A LOA pod, four minutes
              away.
            </p>
            <p className="power20-highlight">
              You walk in. The door closes. The noise drops. The light dims.
            </p>
            <p>Eyes close. Breathing slows. The fog starts to lift.</p>
            <p className="power20-highlight">
              Twenty minutes. Clarity restored. You step back out - different.
            </p>
          </div>

          <div className="power20-close">
            The science is settled. The benefits are real.
            <br />
            <span className="coral">What's missing is the space.</span>
          </div>
        </div>
      </section>

      {/* CBS2 Section — compact CTA */}
      <section
        className="cas2-section cbs2-section cbs2-compact"
        data-section-id="cbs2"
        ref={cbs2Ref}
      >
        <div
          className={`cas2-content cbs2-content ${
            visibleSections.has("cbs2") ? "visible" : ""
          }`}
        >
          <div className="cbs2-header">Help us build it</div>
          <p className="cbs2-subtext">
            Be part of creating the infrastructure cities need.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CAS2;
